import { useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowDropDown } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Typography,
  Alert,
} from "@mui/material";
import { partition, truncate } from "lodash";
import { useSnackbar } from "notistack";
import { ReactElement, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import {
  AudienceType,
  CreateNonOptInAudienceMutation,
  CreateNonOptInAudienceMutationVariables,
  GetAudienceContactFieldsQuery,
  GetAudienceQuery,
  GetAudienceQueryVariables,
  GetAudienceSearchUserFilterQuery,
  GetAudienceSearchUserFilterQueryVariables,
  ListAudiencesForSubaccountQuery,
  ListAudiencesForSubaccountQueryVariables,
  AudienceFieldsFragment,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import {
  ButtonLink,
  ExternalLink,
  InternalLink,
  LinkButton,
} from "~frontend/components/Link";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import { RhfTextField } from "~frontend/components/forms/react-hook-form-components";
import {
  CREATE_NON_OPT_IN_AUDIENCE,
  GET_AUDIENCE,
  GET_AUDIENCE_CONTACT_FIELDS,
  GET_AUDIENCE_SEARCH_USER_FILTER,
  LIST_AUDIENCES_FOR_SUBACCOUNT,
} from "~frontend/graphql/audience";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { Audience } from "~frontend/routes/subaccounts/audiences/Subscribers/types";
import theme, { palette } from "~frontend/theme";
import { formatShortDateTime } from "~frontend/util/dateFormat";
import { jobFailed, jobInProgress } from "~frontend/util/jobs";
import useMutation from "~frontend/util/useMutation";

import { NumberStatusTag } from "../phoneNumberList/PhoneNumberTable";
import {
  AudienceNameFormData,
  AudienceNameFormSchema,
} from "./AudienceSettings";
import { AudienceSubscribersTable } from "./AudienceSubscriberTable";
import { AudienceSummary } from "./AudienceSummary";
import AddSubscriberDialog from "./Subscribers/AddSubscriberDialog";
import JobDetailsDialog from "./Subscribers/JobDetailsDialog";
import { SMALL_BUTTON_STYLE } from "./constants";
import { useAudience } from "./store";

function AudienceLabel({
  audience,
  slim = false,
}: {
  audience?: AudienceFieldsFragment | null;
  slim?: boolean;
}): ReactElement | undefined {
  if (!audience) {
    return;
  }

  const { type, name, archivedAt } = audience;
  const isArchived = !!archivedAt;

  const displayLabel =
    type === AudienceType.Subscribers ? (
      <>
        <span>Subscribers</span>
        <NumberStatusTag
          backgroundColor={palette.lightOrange}
          color={palette.navy}
        >
          Opt In
        </NumberStatusTag>
      </>
    ) : (
      <>{slim ? truncate(name || "", { length: 80 }) : name}</>
    );

  return (
    <FlexBox alignItems="center" sx={{ mt: 0.5 }} gap={1}>
      {displayLabel}
      {isArchived && (
        <NumberStatusTag
          backgroundColor={palette.lightGray}
          color={palette.navy}
        >
          Archived
        </NumberStatusTag>
      )}
    </FlexBox>
  );
}

function AudienceDropdown({
  audiences,
  currentAudience,
}: {
  audiences: ListAudiencesForSubaccountQuery["listAudiencesForSubaccount"];
  currentAudience?: GetAudienceQuery["getAudience"];
}): ReactElement {
  const { subaccountLink } = useSubaccount()!;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  function handleCloseMenu() {
    setAnchorEl(null);
  }

  // Determine if we are looking at an audience that isn't already in the dropdown
  // (i.e. looking at an archived audience) so we can manually add it
  const audienceNeedsAdded =
    !!currentAudience?.id && !audiences.find(a => a.id === currentAudience.id);

  return (
    <>
      <Button
        onClick={event => setAnchorEl(event.currentTarget)}
        endIcon={<ArrowDropDown />}
        sx={{
          textTransform: "none",
          color: "inherit",
          minWidth: "auto",
          padding: 0,
          "&:hover": {
            backgroundColor: "transparent",
          },
          borderBottom: `1px solid ${palette.midGray}`,
          mb: -1,
        }}
      >
        <Typography variant="h2" sx={{ fontWeight: 600 }}>
          <AudienceLabel audience={currentAudience} slim />
        </Typography>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleCloseMenu}
        onClick={handleCloseMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {audienceNeedsAdded && (
          <MenuItem
            key={currentAudience.id}
            component={InternalLink}
            to={subaccountLink(`/audiences/${currentAudience.id}`)}
            selected={true}
          >
            <Typography variant="inherit" component={"div"}>
              <AudienceLabel audience={currentAudience} />
            </Typography>
          </MenuItem>
        )}
        {audiences.map(audience => (
          <MenuItem
            key={audience.id}
            component={InternalLink}
            to={subaccountLink(`/audiences/${audience.id}`)}
            selected={audience.id === currentAudience?.id}
          >
            <Typography variant="inherit" component={"div"}>
              <AudienceLabel audience={audience} />
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function AudienceSettingsLink({ audienceId }: { audienceId: string }) {
  const { subaccountLink } = useSubaccount()!;
  return (
    <ButtonLink
      sx={{
        fontSize: "10px",
        letterSpacing: "0.4px",
        px: 1,
        py: 0.75,
        height: "30px",
        fontWeight: 500,
      }}
      to={subaccountLink(`/audiences/${audienceId}/settings`)}
      variant="contained"
      color="tertiary"
    >
      Audience Settings
    </ButtonLink>
  );
}

function AudienceArchivedPageHeader({
  audience,
}: {
  audience: GetAudienceQuery["getAudience"];
}): ReactElement {
  return (
    <>
      <Alert severity="warning">
        This audience has been archived. To restore it, contact support to get a
        new opt-in number added to your account.
      </Alert>
      <FlexBox gap={4} justifyContent="space-between" alignItems="center">
        <FlexBox gap={2} alignItems="center">
          <AudienceSummary audience={audience} />
        </FlexBox>
        <FlexBox gap={1}>
          <AudienceSettingsLink audienceId={audience.id} />
        </FlexBox>
      </FlexBox>
    </>
  );
}

function AudiencePageHeader({
  audience,
  audienceContactFields,
}: {
  audience: GetAudienceQuery["getAudience"];
  audienceContactFields: GetAudienceContactFieldsQuery["getAudienceContactFields"];
}): ReactElement {
  const [addSubscriberDialogOpen, setAddSubscriberDialogOpen] = useState(false);
  const [jobDetailsOpen, setJobDetailsOpen] = useState(false);
  const refetchSearchResults = useAudience(store => store.refetchSearchResults);

  function renderImportJob() {
    const importJob = audience?.audienceContactImportJob;
    if (!importJob) {
      return null;
    }

    let message: string;
    if (jobInProgress(importJob)) {
      message = `Upload in progress...`;
    } else if (jobFailed(importJob).failed) {
      message = `Upload Failed: ${formatShortDateTime(importJob.startedAt)}`;
    } else {
      message = `Last Upload: ${formatShortDateTime(importJob.startedAt)}`;
    }
    return (
      <Typography variant="body3">
        {message}{" "}
        <LinkButton onClick={() => setJobDetailsOpen(true)}>
          View Details
        </LinkButton>
      </Typography>
    );
  }

  return (
    <>
      <FlexBox gap={4} justifyContent="space-between" alignItems="center">
        <FlexBox gap={2} alignItems="center">
          <AudienceSummary audience={audience} />
          {renderImportJob()}
        </FlexBox>
        <FlexBox gap={1}>
          <AudienceSettingsLink audienceId={audience.id} />
          <Button
            sx={{
              fontSize: "10px",
              letterSpacing: "0.4px",
              px: 1,
              py: 0.75,
              height: "30px",
              fontWeight: 500,
            }}
            variant="contained"
            color="tertiary"
            onClick={() => setAddSubscriberDialogOpen(true)}
          >
            Add Contacts
          </Button>
        </FlexBox>
      </FlexBox>
      {addSubscriberDialogOpen && (
        <AddSubscriberDialog
          onClose={() => setAddSubscriberDialogOpen(false)}
          audienceId={audience?.id || ""}
          refreshList={async () => {
            if (refetchSearchResults) {
              await refetchSearchResults();
            }
          }}
          openJobDetails={() => setJobDetailsOpen(true)}
          audienceContactFields={audienceContactFields}
          isOptInAudience={!!audience?.shortCodeConfig}
        />
      )}
      {jobDetailsOpen && audience?.audienceContactImportJob && (
        <JobDetailsDialog
          job={audience.audienceContactImportJob}
          onClose={() => setJobDetailsOpen(false)}
          isOptInAudience={audience.type === "SUBSCRIBERS"}
        />
      )}
    </>
  );
}

function AudiencePageContent({
  audience,
}: {
  audience: Audience;
}): ReactElement {
  const isArchived = !!audience?.archivedAt;

  const {
    loading,
    error,
    data: userSearchFilterData,
  } = useQuery<
    GetAudienceSearchUserFilterQuery,
    GetAudienceSearchUserFilterQueryVariables
  >(GET_AUDIENCE_SEARCH_USER_FILTER, {
    variables: {
      input: {
        audienceId: audience?.id || "",
      },
    },
    skip: !audience?.id,
  });

  const { data: audienceFields } = useQuery<GetAudienceContactFieldsQuery>(
    GET_AUDIENCE_CONTACT_FIELDS,
    {
      variables: {
        input: { audienceId: audience?.id || "" },
      },
      skip: !audience?.id,
    }
  );

  return (
    <FlexBox flexDirection="column" gap={2} sx={{ width: "100%" }}>
      {isArchived ? (
        <AudienceArchivedPageHeader audience={audience} />
      ) : (
        <AudiencePageHeader
          audience={audience}
          audienceContactFields={audienceFields?.getAudienceContactFields || []}
        />
      )}

      <AsyncWrapper
        loading={loading}
        errors={error}
        data={userSearchFilterData}
      >
        {({ getAudienceSearchUserFilter }) => (
          <AudienceSubscribersTable
            subscriberAudience={audience}
            userSearchFilter={getAudienceSearchUserFilter}
            fieldDefinitions={audienceFields?.getAudienceContactFields || []}
          />
        )}
      </AsyncWrapper>
    </FlexBox>
  );
}

function NewAudienceButton(): ReactElement {
  const { subaccount, subaccountLink } = useSubaccount()!;
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [createNonOptInAudience] = useMutation<
    CreateNonOptInAudienceMutation,
    CreateNonOptInAudienceMutationVariables
  >(CREATE_NON_OPT_IN_AUDIENCE, {
    refetchQueries: [LIST_AUDIENCES_FOR_SUBACCOUNT],
    awaitRefetchQueries: true,
  });

  const formMethods = useForm<AudienceNameFormData>({
    resolver: zodResolver(AudienceNameFormSchema),
    mode: "onChange",
    values: { name: "" },
  });

  const { formState, handleSubmit, control, reset } = formMethods;

  async function handleCreateAudience(formData: AudienceNameFormData) {
    const { errors, data } = await createNonOptInAudience({
      variables: {
        input: {
          subaccountId: subaccount.id,
          name: formData.name,
        },
      },
    });

    if (errors) {
      enqueueSnackbar("Failed to create audience", { variant: "error" });
      return;
    }

    reset({ name: "" });
    setModalOpen(false);
    void navigate(
      subaccountLink(`/audiences/${data?.createNonOptInAudience?.id}`)
    );
  }

  return (
    <div>
      <Button
        variant="outlined"
        sx={{
          color: palette.navy,
          borderColor: palette.darkGray,
          ...SMALL_BUTTON_STYLE,
        }}
        onClick={() => setModalOpen(true)}
      >
        + New Audience
      </Button>

      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        slotProps={{
          paper: { sx: { padding: theme.spacing(2) } },
        }}
      >
        <form onSubmit={handleSubmit(handleCreateAudience)}>
          <DialogTitle>New Audience</DialogTitle>
          <DialogContent sx={{ paddingBottom: 0 }}>
            <Typography
              variant="body1"
              sx={{ paddingBottom: theme.spacing(1) }}
            >
              If you would like to create an{" "}
              <ExternalLink
                href="https://support.scaletowin.com/running-an-opt-in-program-with-long-code"
                target="_blank"
              >
                opt-in audience
              </ExternalLink>
              , please contact the Scale to Win team.
            </Typography>
            <RhfTextField
              control={control}
              name="name"
              label="Audience Name"
              fullWidth
            />
          </DialogContent>
          <DialogActions sx={{ px: theme.spacing(3) }}>
            <Button
              variant="contained"
              color="tertiary"
              onClick={() => {
                formMethods.reset();
                setModalOpen(false);
              }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={
                !formState.isValid ||
                !formState.isDirty ||
                formState.isSubmitting
              }
              loading={formState.isSubmitting}
            >
              Create Audience
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
}

export function AudiencesPage(): ReactElement {
  const { subaccount, subaccountLink } = useSubaccount()!;
  const { audienceId } = useParams<{ audienceId?: string }>();
  const navigate = useNavigate();

  const { data: audiencesListData, loading: audiencesListLoading } = useQuery<
    ListAudiencesForSubaccountQuery,
    ListAudiencesForSubaccountQueryVariables
  >(LIST_AUDIENCES_FOR_SUBACCOUNT, {
    variables: {
      input: {
        subaccountId: subaccount.id,
      },
    },
  });

  // redirect to subscriberAudience or first audience if no audienceId provided
  useEffect(() => {
    if (!audienceId && audiencesListData?.listAudiencesForSubaccount) {
      const audiences = audiencesListData.listAudiencesForSubaccount;

      const [optInAudiences, regularAudiences] = partition(
        audiences,
        a => a.type === "SUBSCRIBERS"
      );

      optInAudiences.sort((a, b) => (b.archivedAt || 0) - (a.archivedAt || 0));
      regularAudiences.sort(
        (a, b) => (b.archivedAt || 0) - (a.archivedAt || 0)
      );

      const firstActiveOptInAudience = optInAudiences.find(a => !a.archivedAt);
      const firstActiveRegularAudience = regularAudiences.find(
        a => !a.archivedAt
      );

      const defaultAudience =
        firstActiveOptInAudience ??
        firstActiveRegularAudience ??
        optInAudiences?.[0];

      if (defaultAudience) {
        void navigate(subaccountLink(`/audiences/${defaultAudience.id}`), {
          replace: true,
        });
      }
    }
  }, [audienceId, audiencesListData, navigate, subaccountLink]);

  useBreadcrumbs([
    {
      label: "Audiences",
      path: subaccountLink("/audiences"),
    },
  ]);

  const refetchSearchResults = useAudience(store => store.refetchSearchResults);

  const {
    data: audienceData,
    loading: audienceLoading,
    error,
    startPolling,
    stopPolling,
  } = useQuery<GetAudienceQuery, GetAudienceQueryVariables>(GET_AUDIENCE, {
    variables: {
      input: {
        audienceId: audienceId!,
      },
    },
    skip: !audienceId,
  });

  const jobWasRunning = useRef(false);
  useEffect(() => {
    const importJob = audienceData?.getAudience?.audienceContactImportJob;

    if (!importJob) {
      stopPolling();
      return;
    }

    const isRunning = jobInProgress(importJob);
    if (isRunning) {
      startPolling(1000);
    } else {
      stopPolling();
      if (jobWasRunning.current) {
        refetchSearchResults().catch(console.error);
      }
    }

    jobWasRunning.current = isRunning;

    return () => {
      stopPolling();
    };
  }, [
    audienceData?.getAudience?.audienceContactImportJob,
    startPolling,
    stopPolling,
    refetchSearchResults,
  ]);

  const loading = audiencesListLoading || audienceLoading;

  const hasAtLeastOneAudience =
    !loading &&
    audiencesListData?.listAudiencesForSubaccount?.length &&
    audiencesListData.listAudiencesForSubaccount.length > 0;

  function AudienceDropdownOrName() {
    if (
      !audiencesListData?.listAudiencesForSubaccount ||
      audiencesListData.listAudiencesForSubaccount.length === 0
    ) {
      return null;
    }

    if (audiencesListData.listAudiencesForSubaccount.length > 1) {
      return (
        <AudienceDropdown
          audiences={audiencesListData.listAudiencesForSubaccount}
          currentAudience={audienceData?.getAudience}
        />
      );
    }

    const singleAudience = audiencesListData.listAudiencesForSubaccount[0];

    return (
      <Typography
        variant="h2Bold"
        sx={{ fontWeight: 600, fontSize: "16px", pl: 1, color: palette.navy }}
      >
        <AudienceLabel audience={singleAudience} />
      </Typography>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <FlexBox justifyContent="space-between">
          <FlexBox alignItems="center" gap={2}>
            Audiences
            <AudienceDropdownOrName />
          </FlexBox>
          <NewAudienceButton />
        </FlexBox>
      </PageHeader>
      <AsyncWrapper data={audienceData} loading={loading} errors={error}>
        {({ getAudience }) => <AudiencePageContent audience={getAudience} />}
      </AsyncWrapper>
      {!hasAtLeastOneAudience && (
        <FlexBox
          alignItems="center"
          justifyContent="center"
          sx={{ py: 10, px: 5, backgroundColor: palette.white }}
        >
          <Typography variant="body2">
            This subaccount does not have any audiences yet. Use the button
            above to create a new audience.
          </Typography>
        </FlexBox>
      )}
    </PageContainer>
  );
}
